import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBookingList } from '../services/api';
import { DataTable, FormTextField, FormDatePicker, FormSelect } from '../shared';
import moment from 'moment';
import DashboardReportHeader from './common/DashboardReportHeader';
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Link,
  Chip,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircle as CheckCircleIcon,
  Hotel as HotelIcon,
  ExitToApp as ExitToAppIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  ConnectingAirports as ConnectingAirportsIcon,
  MeetingRoom as MeetingRoomIcon,
  Monitor as MonitorIcon,
  DirectionsWalk as DirectionsWalkIcon,
  Groups as GroupsIcon,
  BusinessCenter as BusinessCenterIcon,
  SupportAgent as SupportAgentIcon,
  Api as ApiIcon,
  HelpOutline as HelpOutlineIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import './BookingList.css';
import ChannelImage from './common/ChannelImage';
import { formatHotelDate } from '../utils/dateUtils';
import {
  formatBookingCurrency,
  getPaymentStatusColor,
  getPaymentStatusLabel,
  resolveBookingPaymentDisplay,
} from '../utils/bookingOpsCardHelpers';

const BOOKING_SOURCE_LABELS = {
  direct: 'Direct (Roomnexa)',
  booking_engine: 'Booking Engine (NexaStay)',
  ota: 'OTA (Online Travel Agency)',
  walk_in: 'Walk-In',
  corporate: 'Corporate',
  group: 'Group Booking',
  agent: 'Travel Agent',
  api: 'API Integration',
  other: 'Other',
};

const BOOKING_SOURCE_FILTER_OPTIONS = Object.entries(BOOKING_SOURCE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const PAYMENT_STATUS_FILTER_OPTIONS = [
  { value: 'paid', label: 'Full Payment' },
  { value: 'partial', label: 'Partial Payment' },
  { value: 'unpaid', label: 'Unpaid' },
];

const BOOKING_ACCENT = '#4ce1b6';

const createDefaultBookingListFilters = () => ({
  code: '',
  guest: '',
  phone: '',
  status: '',
  payment_status: [],
  booking_source: [],
  created_at: '',
  check_in_date: '',
  check_in_from: '',
  check_in_to: '',
});

const DEFAULT_BOOKING_LIST_SORT = { column: 'created_at', direction: 'desc' };

function buildBookingListParams(filters, pagination, sortConfig) {
  const sortColumn = sortConfig.column || 'created_at';
  const sortDirection = sortConfig.direction || 'desc';
  const sortParam = sortDirection === 'desc' ? `-${sortColumn}` : sortColumn;

  const params = {
    page: pagination.page + 1,
    per_page: pagination.perPage,
    sort: sortParam,
    // Booking List shows GRP rows once; Stay/ops cards omit this and get member BK cards.
    include_group_rows: 1,
  };

  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'payment_status' || key === 'booking_source') {
      if (Array.isArray(value) && value.length > 0) {
        params[key] = value.join(',');
      }
      return;
    }
    if (key === 'check_in_date' || key === 'check_in_from' || key === 'check_in_to') {
      return;
    }
    if (value !== '' && value != null) {
      params[key] = value;
    }
  });

  if (filters.check_in_date) {
    params.check_in_from = filters.check_in_date;
    params.check_in_to = filters.check_in_date;
  } else {
    if (filters.check_in_from) params.check_in_from = filters.check_in_from;
    if (filters.check_in_to) params.check_in_to = filters.check_in_to;
  }

  return params;
}

function CheckboxMultiFilter({
  label,
  emptyLabel,
  options,
  value = [],
  onChange,
  isDark,
  minWidth = 150,
}) {
  const selected = Array.isArray(value) ? value : [];
  const selectedLabel = useMemo(() => {
    if (!selected.length) return emptyLabel;
    if (selected.length === options.length) return emptyLabel;
    if (selected.length === 1) {
      return options.find((option) => option.value === selected[0])?.label || '1 selected';
    }
    return `${selected.length} selected`;
  }, [selected, options, emptyLabel]);

  return (
    <FormControl size="small" sx={{ minWidth, width: '100%' }}>
      <InputLabel shrink sx={{ color: isDark ? '#999' : '#757575' }}>
        {label}
      </InputLabel>
      <Select
        multiple
        displayEmpty
        value={selected}
        onChange={(event) => {
          const nextValue = event.target.value;
          onChange(typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
        }}
        label={label}
        renderValue={() => selectedLabel}
        sx={{
          color: isDark ? '#ddd' : '#212121',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#333246' : '#e0e0e0' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#555' : '#999' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BOOKING_ACCENT },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              '& .MuiMenuItem-root': {
                color: isDark ? '#ddd' : '#212121',
                '&:hover': { backgroundColor: isDark ? '#38373f' : '#f5f5f5' },
                '&.Mui-selected': {
                  backgroundColor: isDark ? '#38373f' : alpha(BOOKING_ACCENT, 0.12),
                  '&:hover': { backgroundColor: isDark ? '#444350' : alpha(BOOKING_ACCENT, 0.18) },
                },
              },
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox
              checked={selected.includes(option.value)}
              sx={{
                color: isDark ? '#999' : '#757575',
                '&.Mui-checked': { color: BOOKING_ACCENT },
              }}
            />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function PaymentStatusMultiFilter({ value = [], onChange, isDark }) {
  return (
    <CheckboxMultiFilter
      label="Payment"
      emptyLabel="All"
      options={PAYMENT_STATUS_FILTER_OPTIONS}
      value={value}
      onChange={onChange}
      isDark={isDark}
    />
  );
}

function BookingSourceMultiFilter({ value = [], onChange, isDark }) {
  return (
    <CheckboxMultiFilter
      label="Source"
      emptyLabel="All"
      options={BOOKING_SOURCE_FILTER_OPTIONS}
      value={value}
      onChange={onChange}
      isDark={isDark}
      minWidth={180}
    />
  );
}

function formatBookingSourceLabel(raw) {
  const k = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (!k) return '—';
  if (BOOKING_SOURCE_LABELS[k]) return BOOKING_SOURCE_LABELS[k];
  return k
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function bookingSourceIconAndColor(sourceRaw) {
  const src = String(sourceRaw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (src === 'ota') return { Icon: ConnectingAirportsIcon, color: '#2563eb' };
  if (src === 'booking_engine') return { Icon: MeetingRoomIcon, color: '#14b8a6' };
  if (src === 'direct') return { Icon: MonitorIcon, color: '#6366f1' };
  if (src === 'walk_in') return { Icon: DirectionsWalkIcon, color: '#8b5cf6' };
  if (src === 'group') return { Icon: GroupsIcon, color: '#ca8a04' };
  if (src === 'corporate') return { Icon: BusinessCenterIcon, color: '#0d9488' };
  if (src === 'agent') return { Icon: SupportAgentIcon, color: '#db2777' };
  if (src === 'api') return { Icon: ApiIcon, color: '#64748b' };
  return { Icon: HelpOutlineIcon, color: '#64748b' };
}

function formatStayDate(value) {
  if (!value || value === '—') return '—';
  const parsed = moment(value);
  return parsed.isValid() ? formatHotelDate(value) : '—';
}

function StayLine({ Icon, iconColor, label, date }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ lineHeight: 1.2 }}>
      <Icon sx={{ fontSize: 14, color: iconColor, flexShrink: 0, opacity: 0.92 }} />
      <Typography
        component="span"
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.65rem', minWidth: 22 }}
      >
        {label}
      </Typography>
      <Typography component="span" variant="body2" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.78rem' }}>
        {date}
      </Typography>
    </Stack>
  );
}

function BookingStayCell({ row }) {
  const nights = Number(row?.nights || 0);
  const hasNights = Number.isFinite(nights) && nights > 0;
  return (
    <Stack spacing={0.25} sx={{ py: 0.15, minWidth: 155 }}>
      <StayLine
        Icon={LoginIcon}
        iconColor="#4ce1b6"
        label="In"
        date={formatStayDate(row?.check_in_date)}
      />
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <StayLine
          Icon={LogoutIcon}
          iconColor="#60a5fa"
          label="Out"
          date={formatStayDate(row?.check_out_date)}
        />
        {hasNights ? (
          <Typography
            variant="caption"
            sx={{
              ml: 0.25,
              color: '#4ce1b6',
              fontWeight: 800,
              fontSize: '0.62rem',
              whiteSpace: 'nowrap',
            }}
          >
            · {nights}N
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}

function BookingFinanceCell({ row }) {
  const payment = resolveBookingPaymentDisplay(row);
  const statusLabel = getPaymentStatusLabel(payment.paymentStatus);
  const statusColor = getPaymentStatusColor(payment.paymentStatus);
  const showDue = payment.paymentStatus === 'partial_payment' || payment.paymentStatus === 'due';

  if (payment.hasRestaurant) {
    const combinedColor = getPaymentStatusColor(payment.combined?.paymentStatus);
    const combinedDue = Boolean(payment.combined?.isDue);
    return (
      <Stack spacing={0.2} sx={{ py: 0.15, minWidth: 188 }}>
        <Typography variant="caption" sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.58rem', fontWeight: 700, mr: 0.35 }}>H</Box>
          {formatBookingCurrency(payment.hotel?.totalAmount, payment.currency)}
          {payment.hotel?.isDue ? (
            <Box component="span" sx={{ color: '#ff7043', ml: 0.35, fontSize: '0.58rem' }}>due</Box>
          ) : null}
          <Box component="span" sx={{ mx: 0.5, opacity: 0.45 }}>·</Box>
          <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.58rem', fontWeight: 700, mr: 0.35 }}>R</Box>
          {formatBookingCurrency(payment.restaurant?.totalAmount, payment.currency)}
          {payment.restaurant?.isDue ? (
            <Box component="span" sx={{ color: '#ff7043', ml: 0.35, fontSize: '0.58rem' }}>due</Box>
          ) : null}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="nowrap">
          <PaymentsIcon sx={{ fontSize: 14, color: combinedColor, flexShrink: 0, opacity: 0.9 }} />
          <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
            {formatBookingCurrency(payment.combined?.totalAmount, payment.currency)}
          </Typography>
          <Typography
            component="span"
            variant="caption"
            sx={{ color: combinedColor, fontWeight: 700, fontSize: '0.62rem', whiteSpace: 'nowrap' }}
          >
            · {getPaymentStatusLabel(payment.combined?.paymentStatus)}
          </Typography>
          {combinedDue ? (
            <Typography variant="caption" sx={{ color: '#ff7043', fontWeight: 700, fontSize: '0.58rem', whiteSpace: 'nowrap' }}>
              {formatBookingCurrency(payment.combined?.remainingAmount, payment.currency)} due
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.25} sx={{ py: 0.15, minWidth: 168 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="nowrap">
        <PaymentsIcon sx={{ fontSize: 14, color: statusColor, flexShrink: 0, opacity: 0.9 }} />
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
          {formatBookingCurrency(payment.totalAmount, payment.currency)}
        </Typography>
        <Typography
          component="span"
          variant="caption"
          sx={{
            color: statusColor,
            fontWeight: 700,
            fontSize: '0.62rem',
            whiteSpace: 'nowrap',
          }}
        >
          · {statusLabel}
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.25, pl: 2.25 }}>
        <Box component="span" sx={{ color: '#10b981', fontWeight: 600 }}>
          Paid {formatBookingCurrency(payment.totalPaid, payment.currency)}
        </Box>
        {showDue ? (
          <>
            <Box component="span" sx={{ mx: 0.5, opacity: 0.5 }}>
              ·
            </Box>
            <Box component="span" sx={{ color: '#ff7043', fontWeight: 700 }}>
              Due {formatBookingCurrency(payment.remainingAmount, payment.currency)}
            </Box>
          </>
        ) : null}
      </Typography>
    </Stack>
  );
}

const BookingList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [bookingData, setBookingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [toolbarResetKey, setToolbarResetKey] = useState(0);
  const [filters, setFilters] = useState(() => createDefaultBookingListFilters());
  const [sortConfig, setSortConfig] = useState({ ...DEFAULT_BOOKING_LIST_SORT });
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    totalRecords: 0,
  });

  // Fetch booking data
  const fetchBookingData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = buildBookingListParams(filters, pagination, sortConfig);

      const response = await getBookingList(params);
      
      if (response && response.Data) {
        setBookingData(response.Data);
        setPagination(prev => ({
          ...prev,
          totalRecords: response.total_records || 0,
        }));
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch booking data');
    } finally {
      setLoading(false);
    }
  }, [filters, sortConfig, pagination.page, pagination.perPage]);

  useEffect(() => {
    fetchBookingData();
  }, [fetchBookingData]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'check_in_date' && value) {
        next.check_in_from = '';
        next.check_in_to = '';
      }
      return next;
    });
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleDateRangeChange = useCallback((nextStart, nextEnd) => {
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setFilters((prev) => ({
      ...prev,
      check_in_date: '',
      check_in_from: nextStart ? nextStart.format('YYYY-MM-DD') : '',
      check_in_to: nextEnd ? nextEnd.format('YYYY-MM-DD') : '',
    }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleRefreshAndReset = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setFilters(createDefaultBookingListFilters());
    setSortConfig({ ...DEFAULT_BOOKING_LIST_SORT });
    setPagination((prev) => ({ ...prev, page: 0 }));
    setToolbarResetKey((key) => key + 1);
  }, []);

  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (newPerPage) => {
    setPagination(prev => ({ ...prev, perPage: newPerPage, page: 0 }));
  };

  const handleBookingRowClick = useCallback((row) => {
    const isGroup = Boolean(row?.is_group_booking || row?.row_type === 'group_booking');
    if (isGroup) {
      const groupId = Number(row?.group_booking_id || row?.id);
      if (!Number.isInteger(groupId) || groupId <= 0) return;
      navigate(`/booking/group/detail/${groupId}/`);
      return;
    }
    const safeBookingId = Number(row?.id || row?.booking_id);
    if (!Number.isInteger(safeBookingId) || safeBookingId <= 0) return;
    navigate(`/booking/detail/${safeBookingId}/`);
  }, [navigate]);

  const columns = [
    {
      field: 'code',
      label: 'Booking Code',
      sortable: true,
      render: (value, row) => {
        const isGroup = Boolean(row?.is_group_booking || row?.row_type === 'group_booking');
        return (
          <Stack spacing={0.35} sx={{ minWidth: 0 }}>
            <Link
              component="button"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleBookingRowClick(row);
              }}
              sx={{
                textDecoration: 'none',
                fontWeight: 700,
                color: 'primary.main',
                cursor: 'pointer',
                textAlign: 'left',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {value}
            </Link>
            {isGroup ? (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  icon={<GroupsIcon sx={{ fontSize: '0.85rem !important' }} />}
                  label={`${(row.member_booking_codes || []).length || row.booking_count || 0} bookings`}
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    bgcolor: alpha(BOOKING_ACCENT, isDark ? 0.16 : 0.12),
                    color: isDark ? '#e2e8f0' : '#042018',
                    border: `1px solid ${alpha(BOOKING_ACCENT, 0.35)}`,
                    '& .MuiChip-icon': { color: BOOKING_ACCENT },
                  }}
                />
                {(row.member_booking_codes || []).slice(0, 2).map((code) => (
                  <Chip
                    key={code}
                    size="small"
                    label={code}
                    sx={{
                      height: 20,
                      fontSize: '0.62rem',
                      fontWeight: 600,
                      bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#0f172a', 0.04),
                    }}
                  />
                ))}
              </Stack>
            ) : row.group_booking_code ? (
              <Chip
                size="small"
                icon={<GroupsIcon sx={{ fontSize: '0.85rem !important' }} />}
                label={row.group_booking_code}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const gid = Number(row.group_booking_id);
                  if (Number.isInteger(gid) && gid > 0) {
                    navigate(`/booking/group/detail/${gid}/`);
                  }
                }}
                sx={{
                  height: 22,
                  alignSelf: 'flex-start',
                  maxWidth: '100%',
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  bgcolor: alpha(BOOKING_ACCENT, isDark ? 0.16 : 0.12),
                  color: isDark ? '#e2e8f0' : '#042018',
                  border: `1px solid ${alpha(BOOKING_ACCENT, 0.35)}`,
                  cursor: 'pointer',
                  '& .MuiChip-icon': { color: BOOKING_ACCENT },
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            ) : null}
          </Stack>
        );
      },
    },
    {
      field: 'guest_name',
      label: 'Guest',
      sortable: true,
    },
    {
      field: 'guest_phone',
      label: 'Phone',
      sortable: false,
    },
    {
      field: 'created_at',
      label: 'Booking Date',
      sortable: true,
      render: (value) => value && value !== '—' ? formatHotelDate(value) : '—',
    },
    {
      field: 'check_in_date',
      label: 'Stay',
      sortable: true,
      render: (_value, row) => <BookingStayCell row={row} />,
    },
    {
      field: 'total_amount',
      label: 'Finance',
      sortable: true,
      render: (_value, row) => <BookingFinanceCell row={row} />,
    },
    {
      field: 'booking_source',
      label: 'Booking source',
      sortable: false,
      render: (_value, row) => {
        const label =
          row.booking_source_display && String(row.booking_source_display).trim() && row.booking_source_display !== '—'
            ? String(row.booking_source_display).trim()
            : formatBookingSourceLabel(row.booking_source);
        const { Icon, color } = bookingSourceIconAndColor(row.booking_source);
        const src = String(row.booking_source || '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '_');
        const showOtaChannelImage =
          src === 'ota' &&
          row.channel_data != null &&
          row.channel_data.id != null;
        return (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            {showOtaChannelImage ? (
              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }} title={label}>
                <ChannelImage channel={row.channel_data} size={22} />
              </Box>
            ) : (
              <Icon sx={{ fontSize: 22, color, flexShrink: 0 }} titleAccess={label} />
            )}
            <Typography variant="body2" component="span" sx={{ color: 'text.primary' }} noWrap title={label}>
              {label}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'status',
      label: 'Status',
      sortable: true,
      render: (value, row) => {
        const statusConfig = {
          'confirmed': {
            color: '#f57c00',
            bg: 'linear-gradient(135deg, #ffb74d 0%, #ff9800 100%)',
            icon: CheckCircleIcon,
            shadow: '0 4px 12px rgba(255, 152, 0, 0.4)',
          },
          'reserved': {
            color: '#f57c00',
            bg: 'linear-gradient(135deg, #ffb74d 0%, #ff9800 100%)',
            icon: ScheduleIcon,
            shadow: '0 4px 12px rgba(255, 152, 0, 0.4)',
          },
          'checked_in': {
            color: '#2e7d32',
            bg: 'linear-gradient(135deg, #81c784 0%, #4caf50 100%)',
            icon: HotelIcon,
            shadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
          },
          'checked_out': {
            color: '#6b7280',
            bg: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
            icon: ExitToAppIcon,
            shadow: '0 4px 12px rgba(107, 114, 128, 0.4)',
          },
          'cancelled': {
            color: '#ef4444',
            bg: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)',
            icon: CancelIcon,
            shadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
          },
          'pending': {
            color: '#f59e0b',
            bg: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
            icon: ScheduleIcon,
            shadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
          },
          'active': {
            color: '#042018',
            bg: 'linear-gradient(135deg, #4ce1b6 0%, #3ab896 100%)',
            icon: GroupsIcon,
            shadow: '0 4px 12px rgba(76, 225, 182, 0.4)',
          },
          'completed': {
            color: '#6b7280',
            bg: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
            icon: GroupsIcon,
            shadow: '0 4px 12px rgba(107, 114, 128, 0.4)',
          },
        };
        
        const config = statusConfig[row.status_value] || {
          color: '#6b7280',
          bg: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
          icon: ScheduleIcon,
          shadow: '0 4px 12px rgba(107, 114, 128, 0.4)',
        };
        
        const StatusIcon = config.icon;
        
        return (
          <Chip
            icon={<StatusIcon sx={{ fontSize: 16, color: '#000 !important' }} />}
            label={value}
            size="small"
            sx={{
              background: config.bg,
              color: '#000',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
              borderRadius: '14px',
              boxShadow: config.shadow,
              border: '1px solid rgba(255, 255, 255, 0.2)',
              textTransform: 'capitalize',
              transition: 'all 0.2s ease-in-out',
              '& .MuiChip-icon': {
                color: '#000',
                marginLeft: '8px',
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `${config.shadow.replace('0.4', '0.6')}, 0 6px 20px rgba(0,0,0,0.15)`,
              },
            }}
          />
        );
      },
    },
  ];

  // Create filter row cells
  const filterRowCells = columns.map((column) => {
    switch (column.field) {
      case 'code':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Booking Code"
            value={filters.code}
            onChange={(e) => handleFilterChange('code', e.target.value)}
            sx={{ minWidth: 120 }}
          />
        );
      case 'guest_name':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Guest Name/Email"
            value={filters.guest}
            onChange={(e) => handleFilterChange('guest', e.target.value)}
            sx={{ minWidth: 120 }}
          />
        );
      case 'guest_phone':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Phone"
            value={filters.phone}
            onChange={(e) => handleFilterChange('phone', e.target.value)}
            sx={{ minWidth: 120 }}
          />
        );
      case 'created_at':
        return (
          <FormDatePicker
            key={column.field}
            label="Booking Date"
            value={filters.created_at ? moment(filters.created_at) : null}
            onChange={(newValue) => handleFilterChange('created_at', newValue ? newValue.format('YYYY-MM-DD') : '')}
            sx={{ minWidth: 140 }}
          />
        );
      case 'check_in_date':
        return (
          <FormDatePicker
            key={column.field}
            label="Stay Date"
            value={filters.check_in_date ? moment(filters.check_in_date) : null}
            onChange={(newValue) => handleFilterChange('check_in_date', newValue ? newValue.format('YYYY-MM-DD') : '')}
            sx={{ minWidth: 140 }}
          />
        );
      case 'total_amount':
        return (
          <PaymentStatusMultiFilter
            key={column.field}
            value={filters.payment_status}
            onChange={(nextValue) => handleFilterChange('payment_status', nextValue)}
            isDark={isDark}
          />
        );
      case 'status':
        return (
          <FormSelect
            key={column.field}
            label=""
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            displayEmpty
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="confirmed">Confirmed</MenuItem>
            <MenuItem value="checked_in">Checked In</MenuItem>
            <MenuItem value="checked_out">Checked Out</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </FormSelect>
        );
      case 'booking_source':
        return (
          <BookingSourceMultiFilter
            key={column.field}
            value={filters.booking_source}
            onChange={(nextValue) => handleFilterChange('booking_source', nextValue)}
            isDark={isDark}
          />
        );
      default:
        return <div key={column.field}></div>;
    }
  });

  return (
    <Box className="booking-list-container">
      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          {error}
        </Box>
      )}

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={bookingData}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        onSortChange={handleSortChange}
        sortConfig={sortConfig}
        emptyMessage="No bookings found matching the selected filters."
        stickyHeader={true}
        filterRow={filterRowCells}
        onRowClick={handleBookingRowClick}
        toolbarRight={
          <DashboardReportHeader
            key={toolbarResetKey}
            actionsOnly
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={handleDateRangeChange}
            onRefresh={handleRefreshAndReset}
            loading={loading}
            showPdf={false}
            themeMode={isDark ? 'dark' : 'light'}
            accentColor={BOOKING_ACCENT}
            allowAllPreset
            initialPreset="all"
          />
        }
      />
    </Box>
  );
};

export default BookingList;

